import { IdSchema, NamespacedNameSchema, TimestampSchema, TransformSchema, TypeNameSchema, Vec3Schema, } from "@uzverse/core";
import { z } from "zod";
import { MaterialSpecSchema } from "./materials.js";
import { PartIdSchema } from "./parts.js";
export const SCENE_KINDS = [
    "terrain",
    "parcel",
    "building",
    "floor",
    "object",
    "road",
    "marker",
    "avatar",
    "vehicle",
];
export const SceneKindSchema = z.enum(SCENE_KINDS);
export const Sha256Schema = z.string().regex(/^[0-9a-f]{64}$/);
export const SceneGeometrySchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("procedural"),
        generator: NamespacedNameSchema,
        params: z.record(z.string(), z.unknown()),
    }),
    z.object({
        kind: z.literal("asset"),
        assetId: IdSchema,
        sha256: Sha256Schema,
        /** The blob is served at `/api/v1/assets/:sha256`. */
        bounds: z.object({ min: Vec3Schema, max: Vec3Schema }),
    }),
]);
/** The render-ready, public view of one entity (docs/PHASE2_PLAN.md section 2.3). */
export const SceneNodeSchema = z.object({
    /** = entity id. */
    id: IdSchema,
    entityType: TypeNameSchema,
    kind: SceneKindSchema,
    /** Scene parent (may differ from the entity parent). */
    parentId: IdSchema.nullable(),
    /** null = world-owned. */
    ownerCompanyId: IdSchema.nullable(),
    /** World space for roots, parent-relative for children. */
    transform: TransformSchema,
    geometry: SceneGeometrySchema,
    /** Base materials merged with `world.surface` overrides. */
    materials: z.record(PartIdSchema, MaterialSpecSchema),
    label: z.string().max(120).optional(),
    pickable: z.boolean(),
    /** Present only while a `world.construction` is running. */
    construction: z
        .object({
        kind: z.enum(["build", "floor", "demolish"]),
        level: z.number().int().optional(),
        startedAt: TimestampSchema,
        durationMs: z.number().int().positive(),
    })
        .optional(),
    /** Small and public. */
    meta: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
    /** Entity version with surface versions folded in; monotonic per node. */
    version: z.number().int().positive(),
});
export const SceneOpSchema = z.discriminatedUnion("op", [
    z.object({
        op: z.literal("upsert"),
        node: SceneNodeSchema,
        cause: z
            .object({
            actor: z.enum(["human", "agent", "system"]),
            taskId: IdSchema.nullable(),
        })
            .optional(),
    }),
    z.object({ op: z.literal("remove"), id: IdSchema, version: z.number().int().positive() }),
]);
/** Declares how an entity type becomes a scene node (a module manifest `render` hint). */
export const SceneRenderHintsSchema = z.object({
    kind: SceneKindSchema,
    generator: NamespacedNameSchema.optional(),
    params: z.record(z.string(), z.unknown()).optional(),
    paramsFromProps: z.record(z.string(), z.string()).optional(),
    assetProp: z.string().optional(),
    materialsProp: z.string().optional(),
    labelProp: z.string().optional(),
    pickable: z.boolean().default(true),
    anchor: z.enum(["world", "parent"]).default("world"),
});
//# sourceMappingURL=scene.js.map